/**
 * SegmentedControl — a pill-shaped single-select toggle for 2–4 short options
 * (e.g. 代码 / 卡片). Controlled via `value` / `onValueChange`. Implemented as a
 * radiogroup for accessibility; arrow keys move selection within enabled items.
 */
import { forwardRef, useRef, type KeyboardEvent, type ReactNode } from "react";

export interface SegmentedOption<T extends string> {
  readonly value: T;
  readonly label: ReactNode;
  /** Optional leading lucide icon. */
  readonly icon?: ReactNode;
  readonly disabled?: boolean;
}

export interface SegmentedControlProps<T extends string> {
  /** The available options. */
  readonly options: ReadonlyArray<SegmentedOption<T>>;
  /** The selected value (controlled). */
  readonly value: T;
  /** Called with the next value on selection. */
  readonly onValueChange: (value: T) => void;
  /** Accessible group label. */
  readonly "aria-label"?: string;
  readonly className?: string;
}

function SegmentedControlInner<T extends string>(
  { options, value, onValueChange, className, ...rest }: SegmentedControlProps<T>,
  ref: React.Ref<HTMLDivElement>,
) {
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const focusOption = (index: number) => {
    window.requestAnimationFrame(() => itemRefs.current[index]?.focus());
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, optionIndex: number) => {
    const enabled = options
      .map((opt, index) => ({ opt, index }))
      .filter(({ opt }) => !opt.disabled);
    if (enabled.length === 0) return;

    const selectedIndex = enabled.findIndex(({ opt }) => opt.value === value);
    const currentIndex = enabled.findIndex(({ index }) => index === optionIndex);
    const baseIndex = selectedIndex >= 0 ? selectedIndex : Math.max(currentIndex, 0);
    let nextIndex: number | null = null;

    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        nextIndex = (baseIndex + 1) % enabled.length;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        nextIndex = (baseIndex - 1 + enabled.length) % enabled.length;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = enabled.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const next = enabled[nextIndex];
    if (next && next.opt.value !== value) onValueChange(next.opt.value);
    if (next) focusOption(next.index);
  };

  return (
    <div
      ref={ref}
      role="radiogroup"
      aria-label={rest["aria-label"]}
      className={["lc-segmented", className].filter(Boolean).join(" ")}
    >
      {options.map((opt, index) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={opt.disabled}
            data-active={active || undefined}
            className="lc-segmented__item"
            ref={(node) => {
              itemRefs.current[index] = node;
            }}
            tabIndex={active ? 0 : -1}
            onClick={() => {
              if (!opt.disabled && !active) onValueChange(opt.value);
            }}
            onKeyDown={(event) => handleKeyDown(event, index)}
          >
            {opt.icon != null ? <span aria-hidden="true">{opt.icon}</span> : null}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/** Generic forwardRef wrapper preserving the type parameter. */
export const SegmentedControl = forwardRef(SegmentedControlInner) as <T extends string>(
  props: SegmentedControlProps<T> & { ref?: React.Ref<HTMLDivElement> },
) => ReturnType<typeof SegmentedControlInner>;
